//dependencies
import { format, isAfter, parseISO } from "date-fns";
import React from "react";

import { ICellRendererParams, Link, PYColumnDef, Text } from "@performyard/py-components";

//types
import { ReviewForm } from "../../../../../../api/services/searchServiceResultTypes/reviewForm";

//components
//utils
import get from "../../../../../../utils/GetNested";
import { appendCurrentLocationAsFrom } from "../../../../../../utils/reviewsBreadcrumbs";
import { arrayIncludes, findAuthorOrSigner } from "../../../utils";

const ViewButtonCellRenderer = (params: ICellRendererParams<ReviewForm>) => {
	const report = params.data;
	if (!report) return null;
	return (
		<div className="flex h-full items-center">
			<Link
				href={appendCurrentLocationAsFrom(`/review/${report._id}/`)}
				underline="never"
				dangerouslySetClassName="inline-flex h-8 items-center justify-center rounded-1 border-none bg-primary-5 px-2 text-paragraph-3 font-semibold text-neutral-0 hover:bg-primary-7 active:bg-primary-8"
			>
				Open Form
			</Link>
		</div>
	);
};

const SignersDueDateCellRenderer = (params: ICellRendererParams<ReviewForm>) => {
	const report = params.data;
	if (!report) return null;
	const date = get(report, ["signers_due_date"], null);
	const signers = get(report, ["signers"], []);

	if (date && signers?.length > 0 && !(signers?.length === 1 && signers[0] === null)) {
		const parsedDate = parseISO(date);
		return (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					{format(parsedDate, "MMM d, yyyy h:mmaaa")}
				</Text>
			</div>
		);
	}

	return null;
};

const createStatusCellRenderer = (userId: string) => {
	const StatusCellRenderer = (params: ICellRendererParams<ReviewForm>) => {
		const report = params.data;
		if (!report) return null;

		const submit = get(report, ["submit"], false);
		const startDate = get(report, ["start_date"], null);
		const completed = get(report, ["completed"], false);
		const redacted = get(report, ["redacted"], false);
		const authorTurn = get(report, ["author_turn"], 0);
		const signerTurn = get(report, ["signer_turn"], 0);
		const authorIDs = get(report, ["authors"], []);
		const authors = get(report, ["author_objects"], []);
		const signers = get(report, ["signer_objects"], []);
		const pastAuthors = get(report, ["past_authors"], null);
		const pastSigners = get(report, ["past_signers"], null);
		const currentSigner = get(report, ["signer"], null);
		const currentAuthor = get(report, ["author"], null);
		let status = "";

		if (completed) {
			return (
				<div className="flex h-full items-center">
					<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
						Completed
					</Text>
				</div>
			);
		}

		const parsedDate = parseISO(startDate);
		if (startDate && isAfter(parsedDate, new Date())) {
			status = `Starting ${format(parsedDate, "MMM d, yyyy h:mmaaa")}`;
			return (
				<div className="flex h-full items-center">
					<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
						{status}
					</Text>
				</div>
			);
		}

		if (
			!submit &&
			(authorTurn < authorIDs?.length ||
				arrayIncludes(
					authors.map((author) => author._id),
					"_redacted",
					0
				))
		) {
			if (arrayIncludes(pastAuthors, userId, 0)) {
				const currentAuthorInfo = findAuthorOrSigner(get(report, ["author_objects"]), currentAuthor);
				status = `Awaiting submission by ${currentAuthorInfo?.name || "Unassigned"}(Filled out the form)`;
			} else if (
				arrayIncludes(authorIDs, userId, authorTurn) ||
				arrayIncludes(
					authors.map((author) => author._id),
					"_redacted",
					0
				)
			) {
				if (currentAuthor !== userId) {
					const currentAuthorInfo = findAuthorOrSigner(get(report, ["author_objects"]), currentAuthor);
					status = `Awaiting submission by ${
						redacted ? "Anonymous" : currentAuthorInfo?.name || "Unassigned"
					} (Fill out the form)`;
				} else {
					status = "Fill out the form";
				}
			} else {
				try {
					const currentAuthorInfo = findAuthorOrSigner(get(report, ["author_objects"]), currentAuthor);
					status = `Awaiting submission by ${
						redacted ? "Anonymous" : currentAuthorInfo.name
					} (Sign off the form)`;
				} catch (error) {
					status = "Awaiting submission by Unassigned (Sign off the form)";
				}
			}
		} else if (signerTurn <= signers?.length) {
			if (arrayIncludes(pastAuthors, userId, 0)) {
				if (currentSigner === userId) {
					status = "Sign off the form (Filled out the form)";
				} else {
					const currentSignerInfo = findAuthorOrSigner(get(report, ["signer_objects"]), currentSigner);
					status = `Awaiting sign off by ${currentSignerInfo?.name || "Unassigned"} (Filled out the form)`;
				}
			} else if (arrayIncludes(pastSigners, userId, 0)) {
				const currentSignerInfo = findAuthorOrSigner(get(report, ["signer_objects"]), currentSigner);
				status = `Awaiting sign off by ${currentSignerInfo?.name || "Unassigned"} (Signed off the form)`;
			} else if (currentSigner !== userId) {
				const currentSignerInfo = findAuthorOrSigner(get(report, ["signer_objects"]), currentSigner);
				status = `Awaiting sign off by ${currentSignerInfo?.name || "Unassigned"}`;
			} else {
				status = "Sign off the form";
			}
		}

		return (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					{status}
				</Text>
			</div>
		);
	};
	return StatusCellRenderer;
};

export const FormsINeedToDoTableCols = (userId: string): PYColumnDef<ReviewForm>[] => {
	return [
		{
			headerName: "",
			field: "view",
			cellRenderer: ViewButtonCellRenderer,
			flex: 1,
			minWidth: 110,
			sortable: false,
			resizable: false,
		},
		{
			headerName: "Subject",
			field: "subject",
			type: "employee",
			flex: 2,
			minWidth: 130,
			sortable: false,
			resizable: false,
			valueGetter: (p) => ({
				_id: get(p.data, ["subject"], ""),
				name: get(p.data, ["subject_name"], ""),
			}),
		},
		{
			headerName: "Form",
			field: "form_name",
			type: "text",
			flex: 4,
			minWidth: 170,
			sortable: false,
			resizable: false,
			valueGetter: (p) => p.data?.form_name ?? "",
		},
		{
			headerName: "Author's Due Date",
			field: "authors_due_date",
			type: "date",
			flex: 2,
			minWidth: 130,
			sortable: true,
			resizable: false,
			cellRendererParams: {
				dateFormat: "MMM d, yyyy h:mmaaa",
			},
		},
		{
			headerName: "Signer's Due Date",
			field: "signers_due_date",
			cellRenderer: SignersDueDateCellRenderer,
			flex: 2,
			minWidth: 130,
			sortable: true,
			resizable: false,
		},
		{
			headerName: "Status",
			field: "status",
			cellRenderer: createStatusCellRenderer(userId),
			flex: 6,
			minWidth: 180,
			sortable: false,
			resizable: false,
		},
	];
};

export default FormsINeedToDoTableCols;
